import { useState } from "react";
import { motion } from "framer-motion";
import {
  Bot,
  CheckCircle2,
  Copy,
  ExternalLink,
  MessageSquare,
  Package,
  Radio,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useLanguageStore } from "@/stores/languageStore";

type ConnectionKind = "channel" | "group";

interface TelegramConnectionGuideProps {
  kind: ConnectionKind;
  botUsername?: string;
}

interface GuideStep {
  icon: LucideIcon;
  title: string;
  titleAm: string;
  body: string;
  bodyAm: string;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

const COPY_RESET_MS = 1600;

const CHANNEL_STEPS: GuideStep[] = [
  {
    icon: Copy,
    title: "Copy the bot handle",
    titleAm: "የቦቱን ስም ይቅዱ",
    body: "Use the button below, then open your channel in Telegram.",
    bodyAm: "ከታች ያለውን ቁልፍ ይጠቀሙ፣ ከዚያ ቻናልዎን በቴሌግራም ይክፈቱ።",
  },
  {
    icon: ShieldCheck,
    title: "Add SARTN as channel admin",
    titleAm: "SARTNን የቻናል አስተዳዳሪ ያድርጉ",
    body: "Channel info -> Administrators -> Add admin. Enable Post Messages and Edit Messages.",
    bodyAm: "የቻናል መረጃ -> አስተዳዳሪዎች -> አስተዳዳሪ አክል። መልዕክት መላክና ማረም ያብሩ።",
  },
  {
    icon: Package,
    title: "Use your products in posts",
    titleAm: "ምርቶችዎን በፖስት ውስጥ ይጠቀሙ",
    body: "After products are saved in Business, SARTN can help write channel posts for them.",
    bodyAm: "ምርቶች በንግድ ገጽ ከተቀመጡ በኋላ SARTN ለእነሱ የቻናል ፖስት ያዘጋጃል።",
  },
];

const GROUP_STEPS: GuideStep[] = [
  {
    icon: ExternalLink,
    title: "Tap Add to group",
    titleAm: "ወደ ቡድን አክልን ይንኩ",
    body: "Telegram opens and asks which group should receive SARTN.",
    bodyAm: "ቴሌግራም ይከፈታል እና SARTN የሚጨመርበትን ቡድን ይጠይቃል።",
  },
  {
    icon: ShieldCheck,
    title: "Promote the bot to admin",
    titleAm: "ቦቱን ወደ አስተዳዳሪ ያሳድጉ",
    body: "Enable Delete Messages, Ban Users, Pin Messages, and Invite Users.",
    bodyAm: "መልዕክት መሰረዝ፣ ተጠቃሚ ማገድ፣ መልዕክት መሰካትና መጋበዝን ያብሩ።",
  },
  {
    icon: MessageSquare,
    title: "Manage rules from here",
    titleAm: "ህጎችን ከዚህ ያስተዳድሩ",
    body: "Come back to SARTN to set welcome messages, spam filters, and moderation rules.",
    bodyAm: "የእንኳን ደህና መጣችሁ፣ ስፓም ማጣሪያና የቁጥጥር ህጎችን ለማዘጋጀት ወደ SARTN ይመለሱ።",
  },
];

export function TelegramConnectionGuide({
  kind,
  botUsername = "SartnBot",
}: TelegramConnectionGuideProps) {
  const language = useLanguageStore((state) => state.language);
  const [copied, setCopied] = useState(false);
  const isGroup = kind === "group";
  const botHandle = `@${botUsername}`;
  const steps = isGroup ? GROUP_STEPS : CHANNEL_STEPS;
  const telegramUrl = isGroup
    ? `https://t.me/${botUsername}?startgroup=connect_group`
    : `https://t.me/${botUsername}`;

  async function copyBotHandle() {
    await navigator.clipboard.writeText(botHandle);
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPY_RESET_MS);
  }

  function openTelegram() {
    window.open(telegramUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <section className="mt-2 flex flex-col gap-4">
      <div className="rounded-3xl bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
          {isGroup ? <MessageSquare className="h-6 w-6" /> : <Radio className="h-6 w-6" />}
        </div>
        <h2 className="font-headline text-xl font-bold tracking-tight text-[var(--color-text)]">
          {localize(
            isGroup ? "Connect a Telegram group" : "Connect a Telegram channel",
            isGroup ? "የቴሌግራም ቡድን ያገናኙ" : "የቴሌግራም ቻናል ያገናኙ",
            language,
          )}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
          {localize(
            isGroup
              ? "Add the bot once. Then manage welcome messages, spam rules, and customer conversations here."
              : "Add the bot as admin once. Then schedule posts, use product content, and track channel activity here.",
            isGroup
              ? "ቦቱን አንዴ ያክሉ። ከዚያ የእንኳን ደህና መጣችሁ፣ ስፓም ህጎችና የደንበኛ ውይይቶችን ከዚህ ያስተዳድሩ።"
              : "ቦቱን አንዴ እንደ አስተዳዳሪ ያክሉ። ከዚያ ፖስቶችን ያቅዱ፣ የምርት ይዘት ይጠቀሙ፣ የቻናል እንቅስቃሴ ይከታተሉ።",
            language,
          )}
        </p>

        <div className="mt-5 grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={openTelegram}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-semibold text-white active:scale-[0.98]"
          >
            <Bot className="h-4 w-4" />
            {localize(isGroup ? "Add to group" : "Open bot in Telegram", isGroup ? "ወደ ቡድን አክል" : "ቦቱን በቴሌግራም ክፈት", language)}
          </button>
          <button
            type="button"
            onClick={copyBotHandle}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-[var(--color-border)] text-sm font-semibold text-[var(--color-text)] active:scale-[0.98]"
          >
            {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            {copied ? localize("Copied", "ተቀድቷል", language) : `${localize("Copy", "ቅዳ", language)} ${botHandle}`}
          </button>
        </div>
      </div>

      <div className="grid gap-3">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          return (
            <motion.article
              key={step.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04, duration: 0.2 }}
              className="flex gap-3 rounded-2xl bg-[var(--color-surface)] p-4"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--color-bg)] text-[var(--color-primary)]">
                <StepIcon className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text)]">
                  {index + 1}. {localize(step.title, step.titleAm, language)}
                </h3>
                <p className="mt-1 text-xs leading-5 text-[var(--color-text-secondary)]">
                  {localize(step.body, step.bodyAm, language)}
                </p>
              </div>
            </motion.article>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => { window.location.hash = "/business"; }}
        className={cn(
          "flex min-h-11 items-center justify-center gap-2 rounded-2xl",
          "bg-[var(--color-surface)] text-sm font-semibold text-[var(--color-text)] active:scale-[0.98]",
        )}
      >
        <Package className="h-4 w-4" />
        {localize("Set up products first", "መጀመሪያ ምርቶችን ያዘጋጁ", language)}
      </button>
    </section>
  );
}
